import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  discardHouseRulesDraft,
  getHouseRules,
  HouseRulesState,
  publishHouseRules,
  saveHouseRulesDraft,
} from '../../../../api/stays-checkin-client';
import { StaysApiError } from '../../../../api/stays-client';
import { AppButton } from '../../../button/app-button';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { StatusPill } from '../../../status-pill/status-pill';
import { StateSwitch } from '../../../../motion';
import { FormSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { formatDateTime } from '../../../../function-utils/locale';
import toast from '../../../../utils/toast';
import { palette, spacing, typography } from '../../../../style-utils/theme';

/**
 * THE HOUSE RULES EDITOR (plan S6, the listing's Check-in tab). One draft and
 * one published version per listing; publishing a draft makes a new version
 * and the guests who already signed keep theirs. English only (owner SD-8):
 * a summary (shown on the guest's trip step) and the full text (read and
 * signed on the house-rules step). Plain text, no formatting — line breaks are
 * kept.
 */

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
`;

const H = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Small = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

const History = styled.ul`
  margin: 0;
  padding-left: ${spacing.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

export const HouseRulesEditor: React.FC<{ listingId: string; canManage: boolean }> = ({ listingId, canManage }) => {
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [rules, setRules] = useState<HouseRulesState | null>(null);
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'save' | 'publish' | 'discard' | null>(null);
  const [confirm, setConfirm] = useState(false);

  const read = useCallback(async () => {
    setView('loading');
    try {
      const r = await getHouseRules(listingId);
      setRules(r);
      const base = r.draft || r.published;
      setSummary(base ? base.summary_en : '');
      setBody(base ? base.body_en : '');
      setView('ready');
    } catch {
      setView('error');
    }
  }, [listingId]);

  useEffect(() => {
    void read();
  }, [read]);

  const fail = (err: unknown) => {
    const e = err as StaysApiError;
    if (e && e.status === 400 && e.fields && Object.keys(e.fields).length) setFields(e.fields);
    toast.error(refusalText(e, 'Something went wrong. Please try again.'));
  };

  const save = async (): Promise<boolean> => {
    setBusy('save');
    setFields({});
    try {
      await saveHouseRulesDraft(listingId, { summary_en: summary, body_en: body });
      await read();
      toast.success('Draft saved');

      return true;
    } catch (err) {
      fail(err);

      return false;
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (!(await save())) return;
    setBusy('publish');
    try {
      const out = await publishHouseRules(listingId);
      await read();
      toast.success(`Version ${out.published.version} published`);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
      setConfirm(false);
    }
  };

  const discard = async () => {
    setBusy('discard');
    try {
      await discardHouseRulesDraft(listingId);
      await read();
      toast.success('Draft discarded');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const next = rules ? Math.max(0, ...rules.history.map(h => h.version), rules.draft ? rules.draft.version - 1 : 0) + 1 : 1;

  return (
    <Block aria-labelledby="house-rules-title">
      <Row>
        <H id="house-rules-title">House rules</H>
        {rules && rules.published ? (
          <StatusPill tone="success">{`Version ${rules.published.version} published`}</StatusPill>
        ) : (
          <StatusPill tone="warning">Not published</StatusPill>
        )}
        {rules && rules.draft ? <StatusPill tone="neutral">Draft</StatusPill> : null}
      </Row>
      {rules && !rules.published ? (
        <Small role="status">Guests skip the house rules step until you publish rules.</Small>
      ) : null}
      <StateSwitch state={view}>
        {view === 'loading' ? <FormSkeleton fields={2} /> : null}
        {view === 'error' ? <LoadError message="Couldn't load the house rules" onRetry={() => void read()} /> : null}
        {view === 'ready' ? (
          <>
            <CountedTextarea
              id={`rules-summary-${listingId}`}
              label="Summary (shown before the guest signs)"
              value={summary}
              max={600}
              rows={3}
              readOnly={!canManage}
              hasError={!!fields.summary_en}
              onChange={e => setSummary(e.target.value)}
            />
            {fields.summary_en ? <Small role="alert">{fields.summary_en}</Small> : null}
            <CountedTextarea
              id={`rules-body-${listingId}`}
              label="The house rules"
              value={body}
              max={20000}
              rows={10}
              readOnly={!canManage}
              hasError={!!fields.body_en}
              onChange={e => setBody(e.target.value)}
            />
            {fields.body_en ? <Small role="alert">{fields.body_en}</Small> : null}
            {canManage ? (
              <Row>
                <AppButton variant="secondary" loading={busy === 'save'} loadingLabel="Saving" onClick={() => void save()}>
                  Save draft
                </AppButton>
                <AppButton variant="primary" loading={busy === 'publish'} loadingLabel="Publishing"
                  disabled={!summary.trim() || !body.trim()} onClick={() => setConfirm(true)}>
                  {`Publish version ${next}`}
                </AppButton>
                {rules && rules.draft ? (
                  <AppButton variant="ghost" loading={busy === 'discard'} loadingLabel="Discarding" onClick={() => void discard()}>
                    Discard draft
                  </AppButton>
                ) : null}
              </Row>
            ) : null}
            {rules && rules.history.length ? (
              <History aria-label="Earlier versions">
                {rules.history.map(h => (
                  <li key={h.version}>
                    {`Version ${h.version}: published ${h.published_at ? formatDateTime(h.published_at) : '—'}`}
                    {h.superseded_at ? `, replaced ${formatDateTime(h.superseded_at)}` : ' (current)'}
                  </li>
                ))}
              </History>
            ) : null}
            {rules && rules.signed_count ? <Small>{`Signed by ${rules.signed_count} ${rules.signed_count === 1 ? 'guest' : 'guests'} so far.`}</Small> : null}
          </>
        ) : null}
      </StateSwitch>
      <ConfirmDialog
        open={confirm}
        title={`Publish version ${next}`}
        message="Guests who already signed keep their version. Guests who have not signed yet will sign this one."
        primaryLabel={`Publish version ${next}`}
        dismissLabel="Keep editing"
        onPrimary={publish}
        onDismiss={() => setConfirm(false)}
      />
    </Block>
  );
};
